import { mapResponseToJson, mapResponseToText } from '@atlassiansox/asc-core';
import { fetch } from 'cross-fetch';

import {
  CloudExpandFreeValidationRequest,
  CloudExpandFreeValidationResponse,
  CloudSignUpRequest,
  CloudSignUpResponse,
  FreeBreachRequest,
  FreeBreachResponse,
  ProductEditionByCloudIdRequest,
  ProductEditionByCloudIdResponse,
  MarketplaceAppInstallRequest,
  MarketplaceAppInstallResponse
} from './bxp-express-client.types';

export type BXPExpressClientOptions = {
  readonly baseUrl?: string;
};

export interface IBXPExpressClient {
  getXsrfToken(): Promise<string>;
  cloudSignupAnonymous(
    request: CloudSignUpRequest
  ): Promise<CloudSignUpResponse>;
  cloudExpandFreeValidation(
    request: CloudExpandFreeValidationRequest
  ): Promise<CloudExpandFreeValidationResponse>;
  postBreachStatus(request: FreeBreachRequest): Promise<FreeBreachResponse>;
  productEditionByCloudId(
    request: ProductEditionByCloudIdRequest
  ): Promise<ProductEditionByCloudIdResponse>;
  marketplaceAppInstall(
    request: MarketplaceAppInstallRequest
  ): Promise<MarketplaceAppInstallResponse>;
}

export class BXPExpressClient implements IBXPExpressClient {
  public static DEFAULT_BASE_URL =
    'https://wac.stg.internal.atlassian.com/endpoint';

  private readonly baseUrl: string;

  public constructor({
    baseUrl = BXPExpressClient.DEFAULT_BASE_URL
  }: BXPExpressClientOptions = {}) {
    this.baseUrl = baseUrl;
  }

  public async getXsrfToken(): Promise<string> {
    return mapResponseToText(
      await fetch(`${this.baseUrl}/auth/xsrf`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          accept: 'text/plain'
        }
      })
    );
  }

  public async cloudSignupAnonymous({
    payload,
    cookies
  }: CloudSignUpRequest): Promise<CloudSignUpResponse> {
    return mapResponseToJson(
      await fetch(`${this.baseUrl}/cloud/signupAnon`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'ATL-Cookies': JSON.stringify(cookies)
        },
        body: JSON.stringify(payload),
        credentials: 'include'
      })
    );
  }

  public async marketplaceAppInstall({
    siteName,
    appKey,
    xsrfToken
  }: MarketplaceAppInstallRequest): Promise<MarketplaceAppInstallResponse> {
    return mapResponseToJson(
      await fetch(`${this.baseUrl}/marketplace/app/install`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'xsrf-token': xsrfToken
        },
        body: JSON.stringify({ siteName, appKey }),
        credentials: 'include'
      })
    );
  }

  public async cloudExpandFreeValidation({
    cloudId,
    productKeys,
    edition
  }: CloudExpandFreeValidationRequest): Promise<CloudExpandFreeValidationResponse> {
    return mapResponseToJson(
      await fetch(`${this.baseUrl}/cloud/expand-free-validation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          cloudId,
          productKeys,
          edition
        })
      })
    );
  }

  public async postBreachStatus({
    payload
  }: FreeBreachRequest): Promise<FreeBreachResponse> {
    const options = {
      headers: {
        'content-type': 'application/json'
      },
      method: 'POST',
      body: JSON.stringify(payload)
    };

    return mapResponseToJson(
      await fetch(`${this.baseUrl}/cloud/expand-free-validation`, options)
    );
  }

  public async productEditionByCloudId({
    cloudId,
    productKey
  }: ProductEditionByCloudIdRequest): Promise<ProductEditionByCloudIdResponse> {
    return mapResponseToJson(
      await fetch(`${this.baseUrl}/cloud/edition/${cloudId}/${productKey}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      })
    );
  }
}
